import api from 'apis/restaurant-api/api';
import {
  API_UNLIMITED_MAX_DELIVERY_DISTANCE,
  API_UNLIMITED_TOTAL_BUDGET_SUM,
} from 'modules/campaigns/components/budget-section/constants';
import { CampaignStatus } from 'modules/campaigns/types';

import { Campaign, CampaignConfiguration, CampaignType, PaginatedResponse, WeeklySchedule } from './types';

export const getCampaign = async (merchantId: string, campaignId: string): Promise<Campaign> => {
  const baseUrl = `/v1/merchant-admin/self-service-promotions/${merchantId}/promotions/${campaignId}`;

  const res = await api.get<Campaign>(baseUrl);
  return res.data;
};

export const getCampaigns = async ({
  merchantId: merchantId,
  venueIds: venue_ids,
  page: page,
  pageSize: page_size,
  isCreatedByWolt: created_by_wolt,
  campaignType: campaign_type,
  status: status,
}: {
  merchantId: string;
  venueIds?: string[];
  page?: number;
  pageSize?: number;
  isCreatedByWolt?: boolean;
  campaignType?: CampaignType;
  status?: readonly Exclude<CampaignStatus, CampaignStatus.STOPPED>[];
}): Promise<PaginatedResponse<Campaign>> => {
  const url = `/v1/merchant-admin/self-service-promotions/${merchantId}/promotions`;
  return api
    .get<PaginatedResponse<Campaign>>(url, {
      params: { page, page_size, venue_ids, created_by_wolt, campaign_type, status },
    })
    .then(res => res.data);
};

export const getCampaignConfiguration = async (
  merchantId: string,
  venueIds: string[],
): Promise<CampaignConfiguration> => {
  const url = `/v1/merchant-admin/self-service-promotions/${merchantId}/config_v2`;

  const res = await api.post<CampaignConfiguration>(url, { venue_ids: venueIds });

  return res.data;
};

export type CommonWriteCampaignProps = {
  merchantId: string;
  campaignId?: string;
  minBasketSize: number;
  firstPurchaseOnly?: boolean;
  perUserUsageLimit?: number;
  totalBudgetSum: number | typeof API_UNLIMITED_TOTAL_BUDGET_SUM;
  startDate: string;
  endDate: string;
  currency: Currency;
  venueIds: string[];
  weeklySchedule: WeeklySchedule | null;
};

export type DeliveryDiscountWriteCampaignProps = {
  campaignType: CampaignType.DELIVERY_DISCOUNT;
  deliveryDiscount: {
    amount?: Campaign['basket_discount']['amount'];
    percentage?: Campaign['basket_discount']['percentage'];
    upperLimit?: Campaign['basket_discount']['upper_limit'];
  };
  maxDeliveryDistance: number;
} & CommonWriteCampaignProps;

export type BasketDiscountWriteCampaignProps = {
  campaignType: CampaignType.BASKET_DISCOUNT;
  basketDiscount: {
    amount?: Campaign['basket_discount']['amount'];
    percentage?: Campaign['basket_discount']['percentage'];
    upperLimit?: Campaign['basket_discount']['upper_limit'];
  };
  maxDeliveryDistance: typeof API_UNLIMITED_MAX_DELIVERY_DISTANCE;
} & CommonWriteCampaignProps;

export type ItemDiscount = {
  itemTriggerCount: number;
  targetItem: {
    itemId: string;
    venueId: string;
  };
} & (
  | {
      amount: number;
      percentage: null;
      freeItemsCount: null;
    }
  | {
      amount: null;
      percentage: number;
      freeItemsCount: null;
    }
  | {
      amount: null;
      percentage: null;
      freeItemsCount: number;
    }
);

export type ItemDiscountWriteCampaignProps = {
  campaignType: CampaignType.ITEM_DISCOUNT;
  itemDiscount: ItemDiscount;
  maxDeliveryDistance: typeof API_UNLIMITED_MAX_DELIVERY_DISTANCE;
  isEndOfDayCampaign: boolean;
} & CommonWriteCampaignProps;

export type WriteCampaignProps =
  | DeliveryDiscountWriteCampaignProps
  | BasketDiscountWriteCampaignProps
  | ItemDiscountWriteCampaignProps;

export const writeCampaign = async (props: WriteCampaignProps) => {
  const writeFunc = props.campaignId ? api.patch : api.post;
  const url = props.campaignId
    ? `/v1/merchant-admin/self-service-promotions/${props.merchantId}/promotions/${props.campaignId}`
    : `/v1/merchant-admin/self-service-promotions/${props.merchantId}`;

  const commonData = {
    name: `merchant-admin-${props.merchantId}-${props.campaignType}-${props.startDate}`,
    campaign_type: props.campaignType,
    min_basket_size: props.minBasketSize,
    first_purchase_only: props.firstPurchaseOnly,
    per_user_usage_limit: props.perUserUsageLimit,
    total_budget_sum: props.totalBudgetSum,
    venue_ids: props.venueIds,
    start_date: props.startDate,
    end_date: props.endDate,
    currency: props.currency,
    weekly_schedule: props.weeklySchedule,
    max_delivery_distance: props.maxDeliveryDistance,
  };

  if (props.campaignType === CampaignType.DELIVERY_DISCOUNT) {
    const res = await writeFunc(url, {
      ...commonData,
      delivery_discount: {
        amount: props.deliveryDiscount.amount,
        percentage: props.deliveryDiscount.percentage,
        upper_limit: props.deliveryDiscount.upperLimit,
      },
    });
    return res.data;
  }

  if (props.campaignType === CampaignType.BASKET_DISCOUNT) {
    const res = await writeFunc(url, {
      ...commonData,
      basket_discount: {
        amount: props.basketDiscount.amount,
        percentage: props.basketDiscount.percentage,
        upper_limit: props.basketDiscount.upperLimit,
      },
    });
    return res.data;
  }

  if (props.campaignType === CampaignType.ITEM_DISCOUNT) {
    const res = await writeFunc(url, {
      ...commonData,
      item_discount: {
        item_trigger_count: props.itemDiscount.itemTriggerCount,
        target_item: {
          item_id: props.itemDiscount.targetItem.itemId,
          venue_id: props.itemDiscount.targetItem.venueId,
        },
        amount: props.itemDiscount.amount,
        percentage: props.itemDiscount.percentage,
        free_items_count: props.itemDiscount.freeItemsCount,
      },
      is_end_of_day_campaign: props.isEndOfDayCampaign,
    });
    return res.data;
  }
};

export const updateCampaignEndDate = async ({
  merchantId,
  campaignId,
  endDate,
}: {
  merchantId: string;
  campaignId: string;
  endDate: string;
}) => {
  return api
    .patch(`/v1/merchant-admin/self-service-promotions/${merchantId}/promotions/${campaignId}`, {
      end_date: endDate,
    })
    .then(res => res.data);
};

export const terminateCampaign = async ({ merchantId, campaignId }: { merchantId: string; campaignId: string }) => {
  return api
    .put(`/v1/merchant-admin/self-service-promotions/${merchantId}/promotions/${campaignId}`)
    .then(res => res.data);
};

export const deleteCampaign = async ({ merchantId, campaignId }: { merchantId: string; campaignId: string }) => {
  return api
    .delete(`/v1/merchant-admin/self-service-promotions/${merchantId}/promotions/${campaignId}`)
    .then(res => res.data);
};
